import { useEffect, useState } from 'react';
import useTourRegistration from './hooks/useTourRegistration';
import { TOUR_ANCHORS, anchor } from './tourAnchors';

//The inventory tour's "click a row" step. Not a tour anchor in tourAnchors.js
//because it is a positional selector rather than an id somebody could rename,
//but the recovery chain still compares against it, so it gets a name.
const INVENTORY_FIRST_ROW_FIELD = '#inventory-content #main-table-body tr:first-child .clickable-field';

//Stable identity for the no-tour case: `steps` is a dependency of the consumers'
//render, and a fresh [] every render would churn them.
const NO_STEPS = Object.freeze([]);

/**
 * The tabs this hook has a tour for, paired with their id in tourRegistry's
 * TOURS. Module scope and frozen, so useTabTourRegistrations below can loop
 * over it and still call the same number of hooks on every render.
 *
 * This is the ONLY list of toured tabs. It used to be spelled out in three
 * places — the tab→steps switch, a parallel switch that looked up the first
 * step's target for the readiness probe, and SubNav's three useTourRegistration
 * calls. Missing the second one when adding a tab was silent and nasty: the
 * tour started before the tab had painted, and Joyride sticks at lifecycle
 * "init" forever when its first target is absent (see handleStartTutorial).
 */
export const TAB_TOURS = Object.freeze([
    Object.freeze({ tab: 'tab-dashboard', id: 'dashboard' }),
    Object.freeze({ tab: 'tab-inventory', id: 'inventory' }),
    Object.freeze({ tab: 'tab-sales',     id: 'sales'     }),
]);

/**
 * Registers every tab tour with the help menu, each gated on its own tab.
 * The gate matters because SubNav is mounted for every tab — without it the
 * menu would offer all three as "this screen".
 *
 * The loop is safe despite the rules of hooks: TAB_TOURS is a frozen module
 * constant, so the number of useTourRegistration calls is fixed for the life of
 * the process.
 */
export function useTabTourRegistrations(activeTab, handleStartTutorial) {
    for (const { tab, id } of TAB_TOURS) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useTourRegistration(id, () => handleStartTutorial(tab), activeTab === tab);
    }
}

function useTabJoyride(
    activeTab,
    setActiveTab,
    activeSubMenu,
    setActiveSubMenu,
    disableTabsForTourRef,
    setDisableTabsForTour
) {
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    // Get today's date
    const now = new Date();

    // Create a date 2 months ago
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);

    // Get the month name
    const monthName = twoMonthsAgo.toLocaleString('default', { month: 'long' });

    // Get the first day of that month
    const firstDay = new Date(twoMonthsAgo.getFullYear(), twoMonthsAgo.getMonth(), 1);

    // Get the last day of that month
    const lastDay = new Date(twoMonthsAgo.getFullYear(), twoMonthsAgo.getMonth() + 1, 0);

    // Format the dates (YYYY-MM-DD)
    const formatLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        setDisableTabsForTour(runTour);
    }, [runTour]);

    const firstDayStr = formatLocal(firstDay);
    const lastDayStr = formatLocal(lastDay);

    const stepsDashboard = [
        //FIRST STEP MUST BE AN ELEMENT THAT ALWAYS EXISTS. handleStartTutorial
        //waits on stepsByTab[tab][0].target before running, so a first step that
        //can be absent is a tour that never starts — and the two cards below it
        //are absent for most dealerships most of the time.
        {
            target: '#dashboard',
            title: 'Your dashboard',
            content: 'This is the first thing you see when you sign in: what needs attention today, and how the month is going.',
            disableBeacon: true,
            placement: 'center',
        },
        //Both of these render nothing at all once there is nothing outstanding,
        //and nothing for a user without the permission behind them. That is the
        //normal case for a well-configured dealership, so both steps are
        //expected to be skipped more often than not — handleJoyrideCallback's
        //target_not_found branch advances past them.
        {
            target: '#setup-completion-card',
            title: 'Finishing your setup',
            content: 'Until your dealership is fully set up this card says what is still missing — a logo, contact details, the things your public website needs. It disappears once there is nothing left to do.',
            disableBeacon: true,
        },
        //There was a '#listing-health-card' step here. That card was folded into
        //"Needs attention" on 2026-09-12 and deleted, so the step was deleted
        //with it rather than left pointing at an id nothing renders — what it
        //explained is now in the needs-attention step below. A step whose target
        //never resolves is not a harmless no-op: it is skipped silently by
        //target_not_found, so the tour still "passes" while quietly teaching
        //less than it claims to.
        //Each card used to carry its own "Tour this card" link, launching a
        //second Joyride with three or four steps of its own. Four cards, four
        //extra Joyride instances, and a line of chrome on every card — and the
        //text below was written assuming that link existed, so four of these
        //steps ended by telling people to click something. The per-card tours
        //are gone and what they actually explained has been folded in here.
        //
        //NOTE ON COLOURS: none of these name one. The old stock card's tour said
        //"the red line shows stock purchased locally, and blue shows imported"
        //while the chart drew local in blue — wrong from the day it was written.
        //Series colours now follow the dealership's theme, so ANY colour word
        //here would be wrong for somebody. Describe the series, never the hue.
        {
            target: '#dashboard-kpi-strip',
            title: 'The numbers, at a glance',
            content: 'Stock on hand, what sold this week, what you are owed and how much of it is overdue, and how far through the month you are. These are the same figures the charts below are built from — here as plain numbers, so you do not have to read a chart to get them.',
            disableBeacon: true,
        },
        {
            target: '#needs-attention-card',
            title: 'What to act on today',
            content: 'Overdue accounts, deals that have been paid beyond their agreed price, stock that has been sitting over nine months, enquiries nobody has answered, and vehicles missing the details buyers filter by — those last ones never show up when someone narrows the search on your website, and clicking the group opens the screen that fills the specs in. Each item names the customer or the vehicle rather than just counting them. If it says nothing needs attention, nothing does.',
            disableBeacon: true,
        },
        {
            target: '#credit-collection-card',
            title: 'Collection against target',
            content: 'How much you have collected this month against what was due, with the coloured bar showing whether you are on track. The tabs split it into money that fell due this month and money that was already overdue, and the dropdown switches between shilling amounts and percentage of target.',
            disableBeacon: true,
        },
        {
            target: '#outstanding-credit-card',
            title: 'Outstanding and overdue credit',
            content: 'What is owed to you over time. The four tabs answer different questions: how overdue debt compares with the total, the total on its own, what share of it is overdue, and how much it moved each day. Hover any point for the exact figures.',
        },
        {
            target: '#stock-trends-card',
            title: 'Stock levels',
            content: 'How your stock has changed over time, split into locally sourced and imported units — the two bands are labelled in the key above the chart. Use the dropdown to look further back.',
        },
        {
            target: '#stock-age-card',
            title: 'How long stock has been sitting',
            content: 'The same vehicles as the card above, grouped by how long each has been in the yard. A large bar at the bottom of this list is capital that is not moving.',
        },
        {
            target: '#sales-trends-card',
            title: 'Weekly sales',
            content: 'Sales by week, either split into cash and credit or into locally sourced and imported. The date on the horizontal axis is the START of each week.',
        },
        {
            target: '#sold-economics-card',
            title: 'How your sold units performed',
            content: 'For vehicles actually handed over: how long they took to sell and how much of the asking price they achieved. The median is shown ahead of the average because one car that sat for two years drags an average somewhere no real vehicle has been — when the two are far apart, a few units are holding you back.',
        },
        {
            target: '#monthly-expenditures-card',
            title: 'What the business paid out',
            content: 'Tracker rental, other expenses, demurrage at handover and broker commissions, stacked by month. Credit written off is reported beneath the chart and deliberately kept out of the total, because nothing is actually paid out when a debt is written off.',
        },
    ];

    const stepsInventory = [
        {
            target: '#inventory-content',
            title: 'Inventory Tab',
            content: 'This is the Inventory tab where you can manage and view all vehicle inventory-related information.',
            disableBeacon: true,
        },
        {
            //Absent once every listing is complete, and for anyone without
            //inventory.edit — the callback skips it in both cases.
            target: '#listing-health-banner',
            title: 'Vehicles your website cannot show properly',
            content: 'A listing missing its mileage, transmission or body type cannot be filtered for on your public site, so buyers searching for exactly that car never see it. This bar names how many, and takes you to them.',
            disableBeacon: true,
        },
        {
            target: '#main-table-head',
            title: 'Inventory Table Column Headers',
            content: 'This is the header of your main inventory table containing column titles.',
            disableBeacon: true,
        },
        {
            target: '#main-table-head tr:first-child > *:nth-child(2)',
            title: '1. Dragging Column Titles',
            content: 'Clicking, holding, and dragging any column Title left or right will rearrange columns to suit your preferences.',
            disableBeacon: true,
            spotlightClicks: true,
            placement: 'bottom',
        },
        {
            target: '#main-table-head tr:first-child > *:nth-child(3)',
            title: '2. Right Clicking Column Titles',
            content: 'Right clicking any title gives you a menu for sorting, filtering, hiding, and showing columns.',
            disableBeacon: true,
            spotlightClicks: true,
            placement: 'bottom',
        },
        {
            target: '#main-table-head tr:first-child > *:nth-child(3)',
            title: 'Please try it out!',
            content: 'Right click this title to open the sort and filter menu, then click next to explore the sorting and filtering options.',
            disableBeacon: true,
            spotlightClicks: true,
            styles: {
                options: {
                    overlayColor: 'rgba(0, 0, 0, 0.3)',
                },
            },
            disableOverlay: false,
            placement: 'right',
        },
        // Context Menu Tour Steps (using mock menu in tooltip)
        {
            target: 'body',
            title: 'Sort Column',
            content: 'Clicking "Sort" will sort the column in ascending first, then descending order when you sort again. By sorting days in stock in desc order, for example, you can see where to focus sales efforts.',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'string',
                hasActiveFilter: false,
                highlightItem: 'sort'
            }
        },
        {
            target: 'body',
            title: 'Filter Options',
            content: 'The Filter section lets you narrow down your data. For text columns, you can search for text that contains your query, e.g "Bus" under the "Shape" colum will only show the buses in stock.',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'string',
                hasActiveFilter: false,
                highlightItem: 'filter-text'
            }
        },
        {
            target: 'body',
            title: 'Entering Filter Values',
            content: 'After selecting a filter option, an input field appears where you enter your search value, then click "Apply Filter" to apply it.',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'string',
                hasActiveFilter: false,
                showFilterInput: 'text',
                highlightItem: 'filter-input'
            },
        },
        {
            target: 'body',
            title: 'Apply Filter Button',
            content: 'Once you\'ve entered your filter value, click the "Apply Filter" button to see only matching results.',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'string',
                hasActiveFilter: false,
                showFilterInput: 'text',
                highlightItem: 'apply-filter-button'
            }
        },
        {
            target: 'body',
            title: 'Numerical & Date Filters',
            content: 'For number and date columns, you get additional comparison options like "Greater than", "Less than", and "Between".',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'date',
                hasActiveFilter: false,
                highlightItem: 'filter-between'
            }
        },
        {
            target: 'body',
            title: 'Between Filter',
            content: 'The "Between" filter lets you specify a range by providing two values - a lower and upper bound, e.g start and end dates.',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'date',
                hasActiveFilter: false,
                showFilterInput: 'between',
                highlightItem: 'filter-input'
            }
        },
        {
            target: 'body',
            title: 'Clear Filters',
            content: 'When a filter is active, you can clear just this column\'s filter or clear all filters at once.',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'string',
                hasActiveFilter: true,
                highlightItem: 'clear-filter'
            }
        },
        {
            target: 'body',
            title: 'Column Visibility',
            content: 'Use "Show/Hide Columns" to customize which columns appear, or "Hide This Column" to hide just the current one.',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'string',
                hasActiveFilter: false,
                highlightItem: 'show-hide-columns'
            }
        },
        {
            target: 'body',
            title: 'Column Visibility and Order',
            content: '"Show/Hide Columns" opens a new dialog box for sorting, showing and hiding columns. It has a "Show Help" button you can use to learn how it works.',
            placement: 'center',
            disableBeacon: true,
            showMockContextMenu: true,
            mockContextMenuProps: {
                fieldType: 'string',
                hasActiveFilter: false,
                highlightItem: 'show-hide-columns'
            }
        },
        // Continue with regular table tour
        {
            target: '#main-table-body',
            title: 'Inventory Table',
            content: 'This is the inventory table where you can view all vehicle records. You can sort, filter, and search for specific vehicles using the controls above the table.',
            disableBeacon: true,
        },
        {
            target: '#main-table-body > tr:first-child',
            title: 'Inventory Table Row',
            content: 'You can view different pieces of information about each vehicle in one row.',
            disableBeacon: true,
            placement: 'bottom-start',
            disableScrolling: true,
        },
        {
            target: '#inventory-content #main-table-body tr:first-child',
            title: 'Open vehicle details',
            content: 'Double-click any row to open the vehicle details modal where you can view and edit all information about the vehicle, add payments, mark the vehicle as sold, e.t.c.',
            disableBeacon: true,
            spotlightClicks: false,
            placement: 'bottom',
        },
        //NOT the SheetFooter controls. Inventory is the one sheet that does not
        //render SheetFooter — it has its own action bar and is not
        //server-paginated — so #search-bar, #records-per-page and
        //#sheet-density-toggle genuinely do not exist on this tab. They belong
        //to the Sales and contact-sheet tours.
        {
            target: '#export-open-button',
            title: 'Download this list',
            content: 'Export what you are looking at to Excel. The dialog that opens has a short tour of its own explaining "all pages" and "hidden columns".',
            disableBeacon: true,
            //Floats against the right edge; a tooltip below it overhangs the
            //viewport by ~48px. Same measurement as the contact-sheet tour.
            placement: 'left',
        },
        {
            //Only rendered when there is something to fix and the user holds
            //inventory.edit; skipped otherwise.
            target: '#inventory-fill-specs',
            title: 'Fill in missing specs automatically',
            content: 'The number in brackets is how many vehicles are missing details. This looks each one up in the same reference database the add-vehicle wizard uses and fills in what it finds, so you can review the lot in one pass instead of opening them one by one.',
            disableBeacon: true,
        },
        {
            target: '#inventory-total-donut-chart',
            title: 'Total Vehicles Donut Chart',
            content: 'This chart shows the total number of vehicles in your inventory, categorized by local and imported.',
            disableBeacon: true,
        },
        {
            target: '#inventory-total-donut-chart-key',
            title: 'Vehicles By Origin',
            content: 'This key shows the number of vehicles by origin.',
            disableBeacon: true,
        },
                {
            target: '#inventory-add-new',
            title: 'Add New Vehicle',
            content: 'Click this button to see how to add a new vehicle to your inventory, then click "Next". You will be prompted to select the vehicle origin first.',
            disableBeacon: true,
            spotlightClicks: true,
        },
        {
            target: '#message-box',
            title: 'Please select an origin first',
            content: 'When adding a new vehicle, you must first select the origin (Local or Imported) to ensure the correct form fields are presented.',
            disableBeacon: true,
            spotlightClicks: true,
            placement: 'bottom',
        },
        {
            target: '#vehicle-picker-modal',
            title: 'Vehicle Picker Wizard',
            content: 'Before the form opens, this wizard lets you identify the vehicle from a reference database of makes, models, and variants. Matching specs are pre-filled in the form automatically, saving time and reducing errors. You can browse by make or search directly by model code.',
            disableBeacon: true,
            spotlightClicks: true,
            placement: 'left',
            // react-floater (Popper.js v1): modifier config lives flat inside
            // `options`, NOT nested under a `modifiers` key. `disableFlip` is a
            // direct prop. Without these, Popper shifts the tooltip off-screen
            // trying to avoid a boundary it mis-calculates for fixed-positioned
            // portal elements.
            floaterProps: {
                disableFlip: true,
                options: {
                    preventOverflow: { enabled: false },
                },
            },
        },
        {
            target: '#vehicle-picker-enter-manually',
            title: 'Skip the Wizard',
            content: 'If the vehicle isn\'t in the reference database, or you\'d rather fill in the details yourself, click "Enter manually" to go straight to the blank form. For this tour, click "Enter manually" now to continue.',
            disableBeacon: true,
            spotlightClicks: true,
        },
        {
            target: '#car-details-modal',
            title: 'Vehicle Details Modal',
            content: 'In this modal, you can add vehicles to inventory, or view and edit all details of existing vehicles.',
            disableBeacon: true,
        },
        {
            target: '#car-details-tour-button',
            title: 'Tour the Vehicle Details Modal',
            content: 'Click this button at the end of the current tour to take a new guided tour of the vehicle details modal and learn about all its features.',
            disableBeacon: true,
            spotlightClicks: false,
        }
    ];

    const stepsSales = [
        {
            target: '#tab-sales',
            title: 'Sales Tab',
            content: 'This is the Sales tab where you can manage and view all sales-related information.',
            disableBeacon: true,
        },
                {
            target: '#tab-inventory',
            title: 'Take Inventory Tour First',
            content: `If you haven't yet, please take a tour of the Inventory tab first.`,
            disableBeacon: true,
        },
        {
            target: '#sub-menu-sales',
            title: 'Sales Sub-Menu',
            content: 'This is the sales sub-menu where you can switch between vehicles at different stages of sales.',
            disableBeacon: true,
        },
        {
            target: '#sub-menu-committed',
            title: 'Committed Vehicles',
            content: 'This is the Committed vehicles section where you can view and manage vehicles that have been bought but not yet delivered.',
            disableBeacon: true,
            spotlightpadding: 0,            
        },
        {
            target: '#sub-menu-committed',
            title: 'Vehicles come here after being sold',
            content: `It's where vehicles move to after being sold in the Inventory tab.`,
            disableBeacon: true,
            spotlightpadding: 0,            
        },
        {
            target: '#sub-menu-committed',
            title: 'Including fully paid ones waiting for delivery',
            content: `It includes vehicles that have not reached the agreed release amount yet, as well as those that have, but are pending delivery.`,
            disableBeacon: true,
            spotlightpadding: 0,            
        },
        {
            target: '#sub-menu-cash',
            title: 'Cash Vehicles',
            content: 'This is the Cash vehicles section where you can view vehicles sold on a cash basis. We shall return here shortly.',
            disableBeacon: true,
            disableNext: activeSubMenu !== "Cash",
            spotlightClicks: true,
            spotlightpadding: 1,
        },
        {
            target: '#sub-menu-credit',
            title: 'Credit Vehicles',
            content: 'This is the Credit vehicles section where you can view and manage vehicles sold on credit. We shall return here shortly.',
            disableBeacon: true,
            disableNext: activeSubMenu !== "Credit",
            spotlightClicks: true,
            spotlightpadding: 1,
        },
        {
            target: '#sub-menu-credit-cleared',
            title: 'Credit Cleared Vehicles',
            content: 'This is the Credit Cleared vehicles section where you can view vehicles whose credit has been fully cleared (Paid or written off).',
            disableBeacon: true,
            disableNext: activeSubMenu !== "CreditCleared",
            spotlightClicks: true,
            spotlightpadding: 1,            
        },
        {
            target: '#sub-menu-credit-cleared',
            title: 'Same structure as Cash section',
            content: 'It is structured exactly the same as the Cash section, so we shall only tour the Cash section in detail.',
            disableBeacon: true,
            spotlightpadding: 1,            
        },        
        {
            target: '#sub-menu-committed',
            title: 'Returning to Committed Vehicles',
            content: 'Now, let\'s return to the Committed vehicles section to continue our tour. Please switch back to the Committed tab to continue.',
            disableBeacon: true,
            disableNext: activeSubMenu !== "Committed",
            spotlightClicks: true,
            spotlightpadding: 1,            
        },
        {
            target: "#committed-content",
            title: 'Committed Vehicles Section',
            content: 'This section helps you manage vehicles that have been sold but not yet delivered.',
            disableBeacon: true,
        },
        {
            target: "#committed-content",
            title: 'Includes those waiting to reach down payment',
            content: 'Say a client pays 5m but release amount is 25m, here is where the vehicle waits as they pay.',
            disableBeacon: true,
        },
        {
            target: "#committed-content",
            title: 'And those still being processed',
            content: 'While the vehicle is being prepared for delivery (including registration), it waits here.',
            disableBeacon: true,
        },
        {
            target: "#committed-content",
            title: 'After delivery, what next?',
            content: 'Those sold on cash move to the Cash section, and those on credit to the Credit section.',
            disableBeacon: true,
        },
        {
            target: "#committed-content #main-table-body tr:first-child",
            title: 'Open Vehicle Details',
            content: 'Double-click any row to open the vehicle details modal where you can view and edit all information about the vehicle, add payments, mark the vehicle for delivery, e.t.c.',
            disableBeacon: true,
            spotlightClicks: false,
        },
        {
            target: '#committed-summary',
            title: 'Quick Summary',
            content: 'This summary bar provides key statistics about your committed vehicles, including total count, total value, and more.',
            disableBeacon: true,
        },
        {
            target: '#sub-menu-cash',
            title: "Please switch to the Cash tab",
            content: "Let's explore how to search for vehicles that were sold on a cash basis. Please switch to the Cash tab to continue.",
            disableBeacon: true,
            disableNext: activeSubMenu !== "Cash",
            spotlightClicks: true,
            spotlightpadding: 0,
        },
        {
            target: '#cash-content',
            title: 'Delivered Cash Sales',
            content: 'All cash sales come here after delivery. You can also search old cash sales here by number plate or chassis no.',
            disableBeacon: true,            
        },
        {
            target: '#sales-summary',
            title: 'Summary & Controls',
            content: 'This section contains search, filtering, and pagination controls.',
            disableBeacon: true,
        },
        {
            target: '#date-filters',
            title: 'Date Range Filter',
            content: 'Display vehicles delivered a certain date range using the "From" and "To" date pickers. Click "Filter" to apply or "Clear" to reset all filters.',
            disableBeacon: true,
        },
        {
            target: '#date-filters',
            title: 'For example',
            content: `For example, if you want to see all vehicles sold on cash in ${monthName}, you could select "From" ${firstDayStr} "To" ${lastDayStr}.`,
            disableBeacon: true,
        },
        {
            target: '#search-bar',
            title: 'Quick Search',
            content: 'Search for specific vehicles by number plate or chassis number. The table filters as you type — press Enter if you want it applied immediately.',
            disableBeacon: true,
        },
        {
            target: '#vehicle-count',
            title: 'Vehicles Displayed',
            content: 'This shows the current number of vehicles visible in the table after applying any filters or search.',
            disableBeacon: true,
        },
        {
            target: '#records-per-page',
            title: 'Records Per Page',
            content: 'Choose how many vehicles to display per page. Options include 25, 50, 100, or 200 records.',
            disableBeacon: true,
        },
        {
            target: '#sheet-density-toggle',
            title: 'Roomy or compact rows',
            content: 'The button beside it switches the table between comfortable and compact rows. Compact fits about half again as many records on screen — the difference between scrolling and not, on a long list.',
            disableBeacon: true,
        },
        {
            target: '#page-info',
            title: 'Pagination Info',
            content: 'See how many records are currently displayed and the total number of cash vehicles in the database.',
            disableBeacon: true,
        },
        {
            target: '#pagination-controls',
            title: 'Page Navigation',
            content: 'Move between pages with the arrows, or click a page number to jump straight to it.',
            disableBeacon: true,
        },
        {
            target: '#cash-content #main-table-body tr:first-child',
            title: 'Double-click to view vehicle details',
            content: 'Double-click any row to open and view its full details.',
            disableBeacon: true,
            spotlightClicks: false,
        },
        {
            target: '#sub-menu-credit',
            title: 'Please switch to the Credit tab',
            content: "Let's explore the unique features of the Credit vehicles section. Please switch to the Credit tab to continue.",
            disableBeacon: true,
            disableNext: activeSubMenu !== "Credit",
            spotlightClicks: true,
            spotlightpadding: 0,
        },
        {
            target: "#credit-content",
            title: 'Credit Vehicles Section',
            content: "If you sell vehicles on credit, this section helps you manage tracking and collection easily.",
            disableBeacon: true,
        },
        {
            target: "#credit-content",
            title: "Two ways vehicles get here: 1",
            content: "1. From the 'Committed' section when they reach down payment.",            
        },
        {
            target: "#credit-content",
            title: "Two ways vehicles get here: 2",
            content: "2. Directly from 'Inventory' when they're of 'local' origin (i.e they are already registered) AND have reached the release amount.",            
        },
        {
            target: "#credit-content",
            title: 'Spot Best and Worst Performers',
            content: "Easily identify top and bottom customers based on payment performance based on metrics like average monthly payment, last payment date, and payment 'performance score (%)'.",
            disableBeacon: true,
        },
        {
            target: "#main-table-head tr:first-child",
            title: "Important columns to monitor performance",
            content: "Let's go through a few performance metrics useful for monitoring credit clients"
        },
        {
            target: "#main-table-head tr:first-child",
            title: "You can sort and filter these columns",
            content: "By right clicking the column titles, you can sort and filter these columns to find top and bottom performers quickly.",
            disableBeacon: true,            
        },
        {
            target: "#main-table-head tr:first-child #thid-MonthlyPayment",
            title: "Agreed Monthly Payment",
            content: "This is the amount the customer agreed to pay monthly when the sale was made. Compare it against Average Monthly Payment.",
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: "#main-table-head tr:first-child #thid-AvgMonthlyPayment",
            title: "Average Monthly Payment",
            content: "Shows the average amount the customer pays monthly. This is idealy supposed to be equal to or greater than their agreed 'Monthly Payment'.",
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: "#main-table-head tr:first-child #thid-PaymentPerformanceScore",
            title: "Payment Performance Score (%)",
            content: "This score is a percentage calculated from (Average Monthly Payment)/(Promised Monthly Payment) * 100. It indicates how well the customer is keeping up with their payment schedule. ",
            disableBeacon: true,
            disableScrolling: true,
        },        
        {
            target: "#main-table-head tr:first-child #thid-PaymentPerformanceScore",
            title: "What the score means",
            content: "A high score (close to 100%) means they're paying on time and in full, while a low score indicates missed or late payments, or lower amounts than the agreed monthly payments. If a customer promised 2M per month but only pays 1M on average, their score will be 50%.",
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: "#main-table-head tr:first-child #thid-LastPmtDate",
            title: "Last Payment Date",
            content: "Shows when the last payment was made. You can right click and sort this column in ascending order to see who have taken longest without paying.",
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: "#main-table-head tr:first-child #thid-DaysOverdue",
            title: "Days Overdue",
            content: "Shows how many days the past the agreed due date. Negative numbers means days remaining until the due date.",
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: "#main-table-head tr:first-child #thid-TotalPenalty",
            title: "Total Penalty",
            content: "If you set a daily penalty, it accumulates for every day the customer has exceeded their due date.",
            disableBeacon: true,
            disableScrolling: true,
        },
        {
            target: "#credit-summary",
            title: "Credit Summary",
            content: "This is fairly self explanatory, so moving on...",
            disableBeacon: true,
        },
        {
            target: "#message-filtered-button",
            title: "Messaging Selected Customers",
            content: "You can send text messages to all or some of the credit customers. When you apply a filter to the table first (e.g Last Payment Date is more than 2 months ago), you can message only those customers.",
            disableBeacon: true,
        },
        {
            target: "#message-filtered-button",
            title: "3rd Party Messaging Account Required",
            content: "To use this feature, you need an EgoSMS account. Set the account name and password in Settings > Messaging to proceed.",
            disableBeacon: true,
        },
        {
            target: "#message-filtered-button",
            title: "Let's tour the messaging feature",
            content: "Please click the button to open the messaging box.",
            disableBeacon: true,
            spotlightClicks: true,
        },
        {
            target: "#start-messaging-tour-button",
            title: "Messaging Tour",
            content: "At the end of this tour, you can click this button to take a more detailed tour of the messaging feature.",
            disableBeacon: true,
        }
    ];

    //One mapping, read by both `steps` below and the readiness probe in
    //handleStartTutorial. The switch this replaces also carried six cases
    //(customers, brokers, sellers, payments, reports, settings) that each
    //returned [] exactly like its own default.
    const stepsByTab = {
        "tab-dashboard": stepsDashboard,
        "tab-inventory": stepsInventory,
        "tab-sales":     stepsSales,
    };
    if (import.meta.env.DEV) {
        for (const { tab } of TAB_TOURS) {
            if (!stepsByTab[tab]) {
                throw new Error(`useTabJoyride: TAB_TOURS lists ${tab} but stepsByTab has no steps for it`);
            }
        }
    }

    const steps = stepsByTab[activeTab] ?? NO_STEPS;

    const handleStartTutorial = (tab) => {
        // First switch to the tab if not already there
        if(activeTab !== tab){
            const tabElement = document.getElementById(tab);
            if(tabElement){
                tabElement.click();
            }
           //setActiveTab(tab);
        }
        if(tab === "tab-sales" && activeSubMenu !== "Committed"){
            setActiveSubMenu("Committed");
        }
        setStepIndex(0);

        //Wait for the first step's target before running. Starting a tour on a
        //tab that has not painted yet leaves Joyride stuck at lifecycle "init":
        //it looks for the target once, does not find it, and never retries —
        //not even to report target_not_found, so nothing recovers it. This was
        //latent while the only way in was a button that lived on the tab
        //already; starting a tour from the header, on a tab you are not on yet,
        //hits it every time.
        const firstTarget = stepsByTab[tab]?.[0]?.target ?? null;

        const deadline = Date.now() + 4000;
        const startWhenReady = () => {
            if(!firstTarget || document.querySelector(firstTarget)){
                setRunTour(true);
                console.log(`Running ${tab} tutorial...`);
                return;
            }
            if(Date.now() > deadline){
                console.warn(`Gave up waiting for ${firstTarget}; not starting the ${tab} tour.`);
                return;
            }
            setTimeout(startWhenReady, 100);
        };
        setTimeout(startWhenReady, 100);
    };

    const handleJoyrideCallback = (data) => {
        const { status, action, type, index } = data;
        console.log("Joyride callback data: ", data);

        // When the step is about to start, manually scroll the target element into view
        if (type === 'step:before' || (type === 'step:after' && action === 'next')) {
            const step = steps[index];
            const el = document.querySelector(step?.target);

            if (el) {
                const container = el.closest('.flex-grow.overflow-auto.min-h-0');
                if (container) {
                    console.log('Scrolling container to make target visible...');
                    container.scrollTo({
                    left: el.offsetLeft - container.clientWidth / 2,
                    behavior: 'smooth'
                    });
                } else {
                    // Fallback: let the browser scroll both axes
                    el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                }
            }    
        }    

        //If the step with target "#inventory-content #main-table-body tr:first-child .clickable-field"
        //is not found, it means there aren't any vehicles in the table. Ask the user to add some.
        if(type === 'error:target_not_found'){
            const target = steps[index]?.target;
            //Where to go when the vehicle form turns out to be open: the form's
            //own step, or back a step if it isn't there either. Three of the
            //branches below wanted exactly this.
            const fallBackToForm = () => {
                if(document.getElementById(TOUR_ANCHORS.carDetailsModal)){
                    const formStepIndex = steps.findIndex(s => s.target === anchor('carDetailsModal'));
                    if(formStepIndex !== -1){ setStepIndex(formStepIndex); return; }
                }
                setStepIndex(index - 1);
            };

            if(target === INVENTORY_FIRST_ROW_FIELD){
                //No rows to click — jump ahead to the "Add New Vehicle" step.
                const addVehicleStepIndex = steps.findIndex(step => step.target === anchor('inventoryAddNew'));
                if(addVehicleStepIndex !== -1){
                    alert(`You don't have any vehicles in your inventory yet, so we're skipping some steps. Please retake the tour after adding vehicles to see those steps.`);
                    setStepIndex(addVehicleStepIndex);
                } else {
                    setStepIndex(0);
                    return;
                }
            } else if(target === anchor('messageBox')){
                //The origin prompt is gone: either the user answered it and the
                //wizard opened, or they resumed a draft straight into the form.
                console.log('Message box not found. Checking what opened instead.');
                if(document.getElementById(TOUR_ANCHORS.vehiclePickerModal)){
                    setStepIndex(index + 1);
                } else {
                    fallBackToForm();
                }
            } else if(target === anchor('vehiclePickerModal') || target === anchor('vehiclePickerEnterManually')){
                //Wizard closed — completed, or skipped manually.
                fallBackToForm();
            } else if(target === anchor('carDetailsModal') || target === anchor('startMessagingTourButton')){
                setStepIndex(index - 1);
            } else {
                //if a target is not found, just skip to the next step
                console.log('Target not found. Skipping to next step.');
                setStepIndex(index + 1);
            }
        }

        // Handle all closing actions consistently
        if (status === 'finished' || status === 'skipped') {
            setRunTour(false);
            setStepIndex(0);
        } else if (action === 'close' || action === 'stop') {
            setRunTour(false);
            setStepIndex(0);
        } else if (type === 'step:after' && action === 'next') {
            setStepIndex(index + 1);
        } else if (type === 'step:after' && action === 'prev') {
            setStepIndex(index - 1);
        }
    };

    return {
        runTour,
        steps,
        stepIndex,
        handleJoyrideCallback,
        handleStartTutorial
    };
}

export default useTabJoyride;